import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Plus, Send, Trash2, ArrowLeft, Loader2 } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function highlightMentions(text) {
  const parts = text.split(/(@\w+)/g);
  return parts.map((p, i) =>
    p.startsWith("@") ? (
      <span key={i} className="text-[#B85C38] font-medium">{p}</span>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function TeamChat({ currentUser }) {
  const [threads, setThreads] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [composing, setComposing] = useState(false);

  const loadThreads = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/threads");
      setThreads(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load threads");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  if (activeThreadId) {
    return (
      <ThreadView
        threadId={activeThreadId}
        currentUser={currentUser}
        onBack={() => { setActiveThreadId(null); loadThreads(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="team-chat">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-stone-500 mb-1">
            / team chat
          </p>
          <p className="text-sm text-stone-600 max-w-2xl">
            Short threads for anything that isn't tied to a specific contact. Everyone can post, everyone can reply.
            Use @firstname to tag a teammate.
          </p>
        </div>
        <Button
          onClick={() => setComposing(true)}
          className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
          data-testid="new-thread-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New thread
        </Button>
      </div>

      {composing && (
        <NewThreadForm
          onCancel={() => setComposing(false)}
          onCreated={(t) => { setComposing(false); setActiveThreadId(t.id); }}
        />
      )}

      {loadingList ? (
        <div className="py-10 flex items-center justify-center text-stone-500" data-testid="threads-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : threads.length === 0 ? (
        <div className="border border-dashed border-stone-300 rounded-sm p-10 text-center" data-testid="threads-empty">
          <MessageSquare className="h-8 w-8 text-stone-400 mx-auto mb-3" />
          <div className="font-display font-semibold text-stone-900">No threads yet</div>
          <p className="text-sm text-stone-600 mt-1 max-w-md mx-auto">
            Start the first one — ask the board a question, share a win, or coordinate on logistics.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="thread-list">
          {threads.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveThreadId(t.id)}
              className={`w-full text-left border rounded-sm px-4 py-3 bg-white hover:border-[#B85C38] transition-colors ${
                t.unread ? "border-[#B85C38]/60 bg-[#FFF7F1]" : "border-stone-200"
              }`}
              data-testid={`thread-row-${t.id}`}
            >
              <div className="flex items-start gap-3">
                {t.unread && (
                  <span className="h-2 w-2 rounded-full bg-[#B85C38] mt-1.5 shrink-0" data-testid={`thread-unread-${t.id}`} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`font-display font-semibold text-stone-900 ${t.unread ? "" : "font-medium"}`}>
                      {t.title}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500">
                      {t.reply_count} {t.reply_count === 1 ? "reply" : "replies"}
                    </span>
                  </div>
                  <div className="text-xs text-stone-600 mt-0.5">
                    Started by {t.created_by_name} · last reply by {t.last_activity_by_name || t.created_by_name} · {timeAgo(t.last_activity_at)}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewThreadForm({ onCancel, onCreated }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/threads", { title: title.trim(), body: body.trim() });
      toast.success("Thread posted");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-stone-300 rounded-sm p-4 bg-white space-y-2.5" data-testid="new-thread-form">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Thread title (e.g. 'March meeting location')"
        maxLength={120}
        className="rounded-sm border-stone-300"
        data-testid="new-thread-title"
      />
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="What's on your mind? Use @firstname to tag a teammate."
        rows={4}
        maxLength={4000}
        className="rounded-sm border-stone-300 resize-none"
        data-testid="new-thread-body"
      />
      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="outline" size="sm" onClick={onCancel} disabled={busy}
          className="rounded-sm border-stone-300"
          data-testid="new-thread-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm" onClick={submit} disabled={busy || !title.trim() || !body.trim()}
          className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
          data-testid="new-thread-submit"
        >
          <Send className="h-3.5 w-3.5 mr-1.5" /> Post
        </Button>
      </div>
    </div>
  );
}

function ThreadView({ threadId, currentUser, onBack }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const isAdmin = currentUser?.role === "admin";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/threads/${threadId}`);
      setData(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load thread");
    }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const b = reply.trim();
    if (!b) return;
    setBusy(true);
    try {
      await api.post(`/threads/${threadId}/replies`, { body: b });
      setReply("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const removeReply = async (replyId) => {
    if (!confirm("Delete this reply?")) return;
    try {
      await api.delete(`/threads/${threadId}/replies/${replyId}`);
      toast.success("Reply deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const removeThread = async () => {
    if (!confirm("Delete this whole thread? This cannot be undone.")) return;
    try {
      await api.delete(`/threads/${threadId}`);
      toast.success("Thread deleted");
      onBack();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  if (!data) {
    return (
      <div className="py-10 flex items-center justify-center text-stone-500" data-testid="thread-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading thread…
      </div>
    );
  }

  const { thread, replies } = data;
  const canDeleteThread = isAdmin || thread.created_by === currentUser?.id;

  return (
    <div className="space-y-4" data-testid={`thread-view-${threadId}`}>
      <div className="flex items-start gap-3">
        <Button
          variant="outline" size="sm" onClick={onBack}
          className="rounded-sm border-stone-300 shrink-0 mt-1"
          data-testid="thread-back"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold tracking-tight text-xl text-stone-900">{thread.title}</h2>
          <div className="text-xs text-stone-600 mt-0.5">
            Started by {thread.created_by_name} · {timeAgo(thread.created_at)} · {thread.reply_count} {thread.reply_count === 1 ? "reply" : "replies"}
          </div>
        </div>
        {canDeleteThread && (
          <Button
            variant="outline" size="sm" onClick={removeThread}
            className="rounded-sm border-stone-300 text-stone-600 hover:text-red-700 hover:border-red-300 shrink-0"
            data-testid="thread-delete"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
        )}
      </div>

      <div className="space-y-2.5" data-testid="replies-list">
        {replies.map((r, i) => {
          const mine = r.author_id === currentUser?.id;
          const canDelete = mine || isAdmin;
          return (
            <div
              key={r.id}
              className={`border rounded-sm p-3 ${mine ? "border-[#B85C38]/40 bg-[#FFF7F1]" : "border-stone-200 bg-white"}`}
              data-testid={`reply-${i}`}
            >
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium text-stone-900 text-sm">{r.author_name}</span>
                <span className="text-[10px] font-mono text-stone-500">{timeAgo(r.created_at)}</span>
                {canDelete && (
                  <button
                    onClick={() => removeReply(r.id)}
                    className="ml-auto text-stone-400 hover:text-red-700"
                    title="Delete reply"
                    data-testid={`reply-delete-${i}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
              <div className="text-sm text-stone-800 mt-1 whitespace-pre-wrap">
                {highlightMentions(r.body)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="border-t border-stone-200 pt-4">
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Write a reply… use @firstname to tag someone"
          rows={3}
          maxLength={4000}
          className="rounded-sm border-stone-300 resize-none"
          data-testid="reply-input"
        />
        <div className="flex justify-end mt-2">
          <Button
            size="sm" onClick={submit} disabled={busy || !reply.trim()}
            className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
            data-testid="reply-submit"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" /> Reply
          </Button>
        </div>
      </div>
    </div>
  );
}
